import { WithChildren, useStateWithCallback } from "@kea-mod/common";
import * as joint from "jointjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { Interaction, KEAGraphContext } from "./KEAGraphContext";

import { InteractionType, KEAElement, KEAGraph, KEALink, KEAPaper, keaNamespace } from "@kea-mod/jointjs";
import { useUserSettingsContext } from "./UserSettingsContext";
import { LinkConfiguration } from "shared/LinkConfiguration";

interface Props extends WithChildren {}

export const KEAGraphContextProvider = (props: Props) => {
  const [modelingGraph, setModelingGraph] = useStateWithCallback<KEAGraph>(initializeGraph());
  const [modelingPaper, setModelingPaper] = useStateWithCallback<KEAPaper>(
    new KEAPaper({ cellViewNamespace: keaNamespace }),
  );
  const [templateGraph, setTemplateGraph] = useStateWithCallback<KEAGraph>(
    new KEAGraph({}, { cellNamespace: keaNamespace }),
  );
  const [templatePaper, setTemplatePaper] = useStateWithCallback<KEAPaper>(
    new KEAPaper({ cellViewNamespace: keaNamespace }),
  );
  const [modelLoaded, setModelLoaded] = useState<boolean>(true);
  const [past, setPast] = useStateWithCallback<KEAGraph[]>([]);
  const [present, setPresent] = useStateWithCallback<KEAGraph>(
    new KEAGraph({}, { cellNamespace: keaNamespace }).toJSON(),
  );
  const [future, setFuture] = useStateWithCallback<KEAGraph[]>([]);
  const [interactions, setInteraction] = useState<Interaction[]>([]);
  const [linkConfiguration, setLinkConfiguration] = useState<LinkConfiguration>({
    addLabelEnabled: false,
    editPathTypeEnabled: false,
    editSourceMarkerEnabled: false,
    editTargetMarkerEnabled: false,
  });

  const userSettingsContext = useUserSettingsContext();

  useEffect(() => {
    window.addEventListener("keydown", handleKeydown);
    return () => {
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [handleKeydown]);

  function handleKeydown(e: KeyboardEvent) {
    // Accept the Cmd key as well, like copy and paste already do, so undo/redo
    // work with the usual shortcut on macOS.
    const modifier = e.ctrlKey === true || e.metaKey === true;
    if (modifier && e.key === "z") {
      if (canUndo()) {
        undo();
      }
    }
    if (modifier && e.key === "y") {
      if (canRedo()) {
        redo();
      }
    }
  }

  const isCellInDom = (): boolean => {
    const element = document.querySelector('[model-id="myInvisibleCell"]');
    return element ? true : false;
  };

  const loadModel = (json: any, onFinished?: () => void) => {
    setModelLoaded(false);
    modelingGraph.fromJSON(json);
    modelingGraph.getElements().forEach((el) => {
      (el as KEAElement)
        .setPrimaryColor(userSettingsContext.primaryColor)
        .setSecondaryColor(userSettingsContext.secondaryColor)
        .setLabelColor(userSettingsContext.labelColor);
    });
    const interval = setInterval(() => {
      if (isCellInDom()) {
        modelingGraph.removeCells([modelingGraph.getCell("myInvisibleCell")]);
        setModelLoaded(true);
        clearInterval(interval);
        if (onFinished) onFinished();
      }
    }, 100);
  };

  function initializeGraph(): KEAGraph {
    const graph = new KEAGraph({}, { cellNamespace: keaNamespace });
    // The graph outlives every render, so its callbacks must go through a ref
    // that always points to the latest state. Binding pushPresent directly
    // would freeze the very first (empty) history and make undo jump back to
    // the initial state instead of one step.
    graph.setHistoryCallback(() => {
      schedulePushPresent();
    });

    graph.setInteractionCallback(
      (
        interactionType: InteractionType,
        interactionTime: number,
        payload?: KEAGraph | KEALink | KEAElement | undefined,
      ) => {
        addUserInteraction(interactionType, interactionTime, payload);
        schedulePushPresent();
      },
    );

    graph.addHistoryEvents();
    graph.addUserInteractionEvents();
    return graph;
  }

  const setTemplateGraphWrapper = (graph: KEAGraph) => {
    setTemplateGraph(graph, () => {
      templatePaper.dumpViews();
    });
  };

  const addCell = (cell: KEAElement) => {
    modelingGraph.addCell(cell);
  };

  const setModelingGraphWrapper = (graph: KEAGraph) => {
    setModelingGraph(graph, () => {
      modelingPaper.dumpViews();
    });
  };

  const setModelingPaperWraper = (paper: KEAPaper) => {
    setModelingPaper(paper, () => {
      modelingPaper.dumpViews();
    });
  };

  const setTemplatePaperWrapper = (paper: KEAPaper) => {
    setTemplatePaper(paper, () => {
      templatePaper.dumpViews();
    });
  };

  const _setHistory = (past: KEAGraph[], present: KEAGraph, future: KEAGraph[], clb: () => void) => {
    setPast(past);
    setPresent(present, () => {
      clb();
    });
    setFuture(future);
  };

  const resetHistory = (graph: KEAGraph) => {
    setPast([]);
    setPresent(graph);
    setFuture([]);
  };

  const canUndo = (): boolean => {
    return past.length > 0 ? true : false;
  };

  const canRedo = (): boolean => {
    return future.length > 0 ? true : false;
  };

  const undo = (): void => {
    let newPast = [...past];
    let newPresent = newPast.pop();
    let newFuture: Array<KEAGraph> = [...future, present];
    _setHistory(newPast, newPresent!, newFuture, () => {
      modelingGraph.fromJSON(newPresent);
    });
  };

  const redo = (): void => {
    const newFuture = [...future];
    const newPast: Array<KEAGraph> = [...past, present];
    const newPresent = newFuture.pop();
    _setHistory(newPast, newPresent!, newFuture, () => {
      modelingGraph.fromJSON(newPresent);
    });
  };

  const pushPresent = useCallback(() => {
    const snapshot = modelingGraph.toJSON();
    // A report that did not change the model content (for example saving an edit
    // dialog without touching a value) must not create an entry, otherwise undo
    // appears to do nothing for one step. Only the cells are compared; graph
    // attributes such as the selection can hold cell instances and are not part
    // of the model content anyway.
    if (JSON.stringify(snapshot.cells) === JSON.stringify((present as any)?.cells)) return;
    let newPast: Array<KEAGraph> = [...past];
    newPast.push(present);
    setPast(newPast);
    setPresent(snapshot);
    setFuture([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [past, present, setPast, setPresent, setFuture]);

  const pushPresentRef = useRef<() => void>(() => {});
  useEffect(() => {
    pushPresentRef.current = pushPresent;
  }, [pushPresent]);

  // A single user action can report history through two channels at once: adding
  // a cell fires the graph's "add" event while the paper's drop handler calls the
  // callback explicitly as well. Collapse everything that arrives within the same
  // synchronous browser event into one entry, so one action is undone by exactly
  // one undo step and the entry captures the final state of the action.
  const pushPresentPending = useRef(false);
  const schedulePushPresent = useCallback(() => {
    if (pushPresentPending.current) return;
    pushPresentPending.current = true;
    queueMicrotask(() => {
      pushPresentPending.current = false;
      pushPresentRef.current();
    });
  }, []);

  const addUserInteraction = useCallback(
    (
      interactionType: InteractionType,
      interactionTime: number,
      payload?: KEAGraph | KEALink | KEAElement | joint.dia.MarkupJSON,
    ) => {
      const interaction: Interaction = {
        interactionType,
        interactionTime,
        payload: payload ? payload : undefined,
      };
      console.log("Added user interaction:", interaction);
      setInteraction((prevState) => [...prevState, interaction]);
    },
    [setInteraction],
  );

  const getUserInteractions = () => {
    return interactions;
  };

  const clearUserInteractions = () => {
    setInteraction([]);
  };

  return (
    <KEAGraphContext.Provider
      value={{
        modelingGraph,
        modelingPaper,
        templateGraph,
        templatePaper,
        linkConfiguration,
        past,
        present,
        future,
        canUndo,
        undo,
        canRedo: canRedo,
        redo,
        resetHistory,
        pushPresent: schedulePushPresent,
        addCellToModelingGraph: addCell,
        setModelingGraph: setModelingGraphWrapper,
        setModelingPaper: setModelingPaperWraper,
        setTemplateGraph: setTemplateGraphWrapper,
        setTemplatePaper: setTemplatePaperWrapper,
        updateLinkConfiguration: setLinkConfiguration,
        addUserInteraction,
        getUserInteractions,
        clearUserInteractions,
        modelLoaded,
        setModelLoaded,
        loadModel,
      }}
    >
      {props.children}
    </KEAGraphContext.Provider>
  );
};
